import { ArrowLeft, ArrowRight, Check, Loader2, UserRound } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { FileUploader } from "./FileUploader";

export const STEPS = ["Médico", "Tus datos", "¿Cómo te sientes?", "Documentos", "Pago"];

function stepCircleClass(done, active) {
  if (done) return "bg-primary text-white cursor-pointer hover:opacity-80";
  if (active) return "bg-accent text-primary border-2 border-primary";
  return "bg-secondary text-muted-foreground";
}

export function StepIndicator({ step, onStepClick }) {
  return (
    <div className="flex items-center justify-between mb-10" data-testid="wizard-step-indicator">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const active = step === n;
        const done = step > n;
        return (
          <div key={label} className="flex-1 flex flex-col items-center gap-1.5">
            <button
              type="button"
              disabled={!done}
              onClick={() => done && onStepClick?.(n)}
              title={done ? `Volver al paso ${n}: ${label}` : undefined}
              data-testid={`wizard-step-circle-${n}`}
              className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors duration-200 ${stepCircleClass(done, active)}`}
            >
              {done ? <Check className="h-4 w-4" strokeWidth={2} /> : n}
            </button>
            <span className={`text-[11px] hidden sm:block ${active ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function StepSelectPro({ pros, selectedPro, onSelect, onNext }) {
  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">¿Quién lleva tu seguimiento THAG?</p>
      {pros.length === 0 && (
        <div className="border rounded-2xl bg-white p-8 text-center text-sm text-muted-foreground" data-testid="no-professionals-msg">
          Aún no hay profesionales disponibles. Intenta más tarde.
        </div>
      )}
      <div className="grid gap-3">
        {pros.map((p) => (
          <button
            key={p.user_id}
            type="button"
            onClick={() => onSelect(p)}
            data-testid={`select-pro-${p.user_id}`}
            className={`border rounded-2xl bg-white p-5 flex items-center gap-4 text-left transition-colors duration-200 ${
              selectedPro?.user_id === p.user_id ? "border-primary bg-accent/50" : "hover:bg-secondary"
            }`}
          >
            <Avatar className="h-12 w-12 border">
              <AvatarImage src={p.picture || undefined} />
              <AvatarFallback className="bg-accent">
                <UserRound className="h-5 w-5 text-primary" strokeWidth={1.5} />
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-heading font-semibold">
                {(p.professional_profile?.titulo || "")} {p.name}
              </p>
              <p className="text-sm text-muted-foreground">{p.professional_profile?.especialidad}</p>
            </div>
            {selectedPro?.user_id === p.user_id && <Check className="h-5 w-5 text-primary ml-auto" strokeWidth={2} />}
          </button>
        ))}
      </div>
      <div className="flex justify-end pt-4">
        <Button className="rounded-full" disabled={!selectedPro} onClick={onNext} data-testid="wizard-step-1-next">
          Continuar <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  );
}

export function StepPersonal({ personal, setPersonal, whatsapp, setWhatsapp, edad, onBack, onNext }) {
  const waDigits = (whatsapp || "").replace(/\D/g, "");
  const waValid = waDigits.length >= 10; // mínimo 10 dígitos (lada + número)
  const valid = personal.nombres.trim() && personal.apellidos.trim() && personal.fecha_nacimiento && waValid;
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Nombre(s)</Label>
          <Input value={personal.nombres} onChange={(e) => setPersonal({ ...personal, nombres: e.target.value })} data-testid="personal-nombres-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Apellidos</Label>
          <Input value={personal.apellidos} onChange={(e) => setPersonal({ ...personal, apellidos: e.target.value })} data-testid="personal-apellidos-input" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Fecha de nacimiento</Label>
        <Input
          type="date"
          value={personal.fecha_nacimiento}
          onChange={(e) => setPersonal({ ...personal, fecha_nacimiento: e.target.value })}
          data-testid="personal-fecha-input"
        />
        {edad !== null && (
          <p className="text-sm text-primary font-semibold" data-testid="edad-calculada">
            Edad: {edad} años
          </p>
        )}
      </div>
      <div className="space-y-1.5">
        <Label>WhatsApp <span className="text-red-600">*</span></Label>
        <Input
          value={whatsapp}
          onChange={(e) => setWhatsapp(e.target.value)}
          placeholder="+52 55 1234 5678"
          inputMode="tel"
          data-testid="personal-whatsapp-input"
        />
        <p className="text-xs text-muted-foreground">
          Incluye la <span className="font-semibold">lada del país</span> (ej. +52 para México). Tu especialista lo usará para contactarte.
        </p>
        {whatsapp && !waValid && (
          <p className="text-xs text-red-600" data-testid="whatsapp-invalid-hint">
            Ingresa un número válido con lada (mínimo 10 dígitos).
          </p>
        )}
      </div>
      <div className="flex justify-between pt-4">
        <Button variant="ghost" className="rounded-full" onClick={onBack} data-testid="wizard-step-2-back">
          <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} /> Atrás
        </Button>
        <Button className="rounded-full" disabled={!valid} onClick={onNext} data-testid="wizard-step-2-next">
          Continuar <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  );
}

export const SUBJ_REQUIRED = ["meds", "last_application", "before_labs", "peso", "estatura", "changes"];

export function StepSubjective({ subj, setSubj, budget, setBudget, busy, onBack, onNext }) {
  const set = (k) => (e) => setSubj({ ...subj, [k]: e.target.value });
  const complete = SUBJ_REQUIRED.every((k) => (subj[k] || "").trim());
  return (
    <div className="space-y-6">
      <div className="rounded-xl bg-accent/50 border border-primary/20 p-4 text-sm" data-testid="subjective-intro">
        Para poder hacer tu receta, tu especialista necesita saber{" "}
        <span className="font-bold">con exactitud y detalle</span> lo siguiente:
      </div>

      <div className="space-y-1.5">
        <Label>1. Todos los medicamentos que estés tomando en este momento, incluyendo tu THAG y cómo lo estás tomando</Label>
        <Textarea
          rows={3}
          value={subj.meds}
          onChange={set("meds")}
          placeholder="Ej.: Valerato de estradiol 2 mg cada 24 h vía oral, espironolactona 100 mg cada 24 h, sertralina 50 mg..."
          data-testid="subjective-meds"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>2. Fecha de última aplicación</Label>
          <Input
            value={subj.last_application}
            onChange={set("last_application")}
            placeholder="Ej.: 14 de mayo de 2026"
            data-testid="subjective-last-application"
          />
        </div>
        <div className="space-y-1.5">
          <Label>3. Fecha de aplicación antes de laboratorios</Label>
          <Input
            value={subj.before_labs}
            onChange={set("before_labs")}
            placeholder="Ej.: 30 de abril de 2026"
            data-testid="subjective-before-labs"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>4. Peso y estatura actuales</Label>
        <div className="grid grid-cols-2 gap-4">
          <Input value={subj.peso} onChange={set("peso")} placeholder="Peso (ej.: 68 kg)" data-testid="subjective-peso" />
          <Input value={subj.estatura} onChange={set("estatura")} placeholder="Estatura (ej.: 1.70 m)" data-testid="subjective-estatura" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>5. Todos los cambios que hubo este tiempo y cómo te sentiste</Label>
        <p className="text-xs text-muted-foreground">
          Por ejemplo: estado de ánimo, cambio en grasa y en glándulas mamarias, músculo, vello
          facial y corporal, caderas, glúteos, piernas, sangrado y cólicos (si aplica), deseo
          sexual, cambio en genitales, etc.
        </p>
        <Textarea
          rows={6}
          value={subj.changes}
          onChange={set("changes")}
          placeholder="Cuéntale a tu especialista con detalle todos los cambios y cómo te has sentido..."
          data-testid="subjective-changes"
        />
      </div>

      <div className="space-y-1.5">
        <Label>
          6. ¿Con cuánto $ cuentas al mes para tu tratamiento?{" "}
          <span className="text-muted-foreground font-normal">(opcional)</span>
        </Label>
        <Input value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="Ej.: $1,500 MXN al mes" data-testid="budget-input" />
      </div>

      {!complete && (
        <p className="text-xs text-amber-700" data-testid="subjective-incomplete-hint">
          Responde las preguntas 1 a 5 para continuar.
        </p>
      )}
      <div className="flex justify-between pt-2">
        <Button variant="ghost" className="rounded-full" onClick={onBack} data-testid="wizard-step-3-back">
          <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} /> Atrás
        </Button>
        <Button className="rounded-full" disabled={!complete || busy} onClick={onNext} data-testid="wizard-step-3-next">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continuar <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} /></>}
        </Button>
      </div>
    </div>
  );
}

export function StepFiles({ requestId, files, setFiles, onBack, onNext }) {
  const docs = files.filter((f) => f.kind !== "payment_proof");
  return (
    <div className="space-y-4">
      <FileUploader
        requestId={requestId}
        kind="lab"
        label="Laboratorios recientes"
        hint="Biometría hemática, química sanguínea, perfil hormonal, USG, etc."
        files={files}
        onChange={setFiles}
      />
      <FileUploader
        requestId={requestId}
        kind="receta_anterior"
        label="Tu última receta"
        hint="La receta y nota de evolución de tu consulta anterior (si la tienes)."
        files={files}
        onChange={setFiles}
      />
      <div className="flex justify-between pt-4">
        <Button variant="ghost" className="rounded-full" onClick={onBack} data-testid="wizard-step-4-back">
          <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} /> Atrás
        </Button>
        <Button className="rounded-full" disabled={docs.length === 0} onClick={onNext} data-testid="wizard-step-4-next">
          Continuar <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  );
}
